import {Injectable} from '@angular/core';
import {CollectionViewer, DataSource} from '@angular/cdk/collections';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {MatSort} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {Drawing} from '../../classes/drawing';
import {DrawingService} from '../../services/drawing.service';

@Injectable({
    providedIn: 'root'
})
export class DrawingsDataSource implements DataSource<Drawing> {

    private drawingSubject = new BehaviorSubject<Drawing[]>([]);
    private loadingSubject = new BehaviorSubject<boolean>(false);
    orderBy = 'created_at';
    orderDirection = 'desc';
    searchTerm?: string;
    offset: number;
    pageSize: number;
    length: number;
    _paginator: MatPaginator;
    private sourceSubscription: Subscription;
    private _errors = false;
    private _projectId?: number;
    private _drawerInitials?: string;

    constructor(private drawingService: DrawingService) {

    }

    set sort(matSort: MatSort) {
        const handleSort = (field, direction) => {
            this.orderBy = matSort.active;
            this.orderDirection = matSort.direction;
        };
        handleSort(matSort.active, matSort.direction);
        matSort.sortChange.subscribe(sort => {
            handleSort(sort.active, sort.direction);
            this.loadDrawings();
        });
    }

    public set paginator(paginator: MatPaginator) {
        this._paginator = paginator;
        this.offset = paginator.pageIndex * this.pageSize;
        this.pageSize = paginator.pageSize;
        paginator.page.subscribe(page => {
            this.offset = page.pageIndex * this.pageSize;
            this.pageSize = page.pageSize;
            this.loadDrawings();
        });
    }

    public set searchString(searchString: string) {
        this.searchTerm = searchString;
        this.loadDrawings();
    }

    public set errors(errors: boolean) {
        this._errors = errors;
        this.loadDrawings();
    }

    public set projectId(projectId: number) {
        this._projectId = projectId;
        this.loadDrawings();
    }

    public set drawerInitials(drawerInitials: string) {
        this._drawerInitials = drawerInitials;
        this.loadDrawings();
    }

    loadDrawings() {
        this.loadingSubject.next(true);

        this.sourceSubscription?.unsubscribe();
        this.sourceSubscription = this.drawingService.getDrawings(
            this.orderBy,
            this.orderDirection || 'asc',
            this.searchTerm,
            this.offset,
            this.pageSize,
            this._errors,
            this._projectId,
            this._drawerInitials
        )
            .subscribe(drawings => {
                this.drawingSubject.next(drawings.data);
                this.length = drawings.length;
                if (this._paginator) {
                    this._paginator.length = this.length;
                }
            });
    }

    connect(collectionViewer: CollectionViewer): Observable<Drawing[] | ReadonlyArray<Drawing>> {
        return this.drawingSubject.asObservable();
    }

    disconnect(collectionViewer: CollectionViewer): void {
        this.drawingSubject.complete();
        this.loadingSubject.complete();
        this.sourceSubscription?.unsubscribe();
    }
}
